/**
 * Robuste Skalierung der Wachstumsachse der BCG-Matrix (Befund G3).
 *
 * Das Problem: Ein Neukunde, dessen Vorperiode fast null war, erreicht
 * Wachstumswerte im vierstelligen Prozentbereich (im Anwendertest: ~10.500 %).
 * Eine Achse, die sich nach dem Extremwert richtet, drückt alle übrigen Kunden
 * auf die Nulllinie — sie überlagern sich dort und sind nicht mehr anklickbar.
 *
 * Die Entscheidung: **Achse nach der Masse skalieren, Ausreißer am Rand
 * markieren.** Wer außerhalb liegt, verschwindet nicht, sondern sitzt am Rand
 * und wird mit seinem echten Wert beschriftet.
 *
 * ## Warum die Tukey-Zäune und kein festes Perzentil
 *
 * Ein festes Perzentil (etwa P5/P95) hat zwei Nachteile, die hier beide weh
 * tun:
 *
 * 1. Es schneidet **immer** einen festen Anteil ab — auch dann, wenn es gar
 *    keinen Ausreißer gibt. Ein Bereich mit 20 gleichmäßig verteilten Kunden
 *    verlöre grundlos zwei davon an den Rand.
 * 2. Bei kleinen Mengen zieht der Ausreißer das Perzentil selbst mit: Bei
 *    zehn Werten interpoliert P95 zwischen dem zweitgrößten und dem größten
 *    Wert — mit 10.500 % als Größtem landet die Grenze bei über 5.000 % und
 *    die Achse ist wieder unlesbar. Genau der Fall aus dem Anwendertest.
 *
 * Die Tukey-Zäune (Q1 − 1,5·IQR bzw. Q3 + 1,5·IQR) sind ebenfalls
 * perzentilbasiert — sie benutzen das erste und dritte Quartil —, richten sich
 * aber nach der **Streuung der mittleren Hälfte** statt nach einem festen
 * Anteil. Liegen alle Werte eng beieinander, liegen die Zäune außerhalb der
 * Daten und es wird **nichts** abgeschnitten (siehe `clamped`). Das ist die in
 * der Statistik übliche Definition eines Ausreißers und damit auch die, die
 * sich einem Bereichsleiter erklären lässt.
 */

/** Faktor der Tukey-Zäune. 1,5 ist die übliche Definition eines Ausreißers. */
export const AXIS_OUTLIER_IQR_FACTOR = 1.5;

/**
 * Halbe Mindestspanne der Achse (5 Prozentpunkte), falls alle Werte identisch
 * sind. Ohne sie wäre die Domäne ein Punkt und die Achse degeneriert.
 */
export const AXIS_MIN_HALF_SPAN = 0.05;

/** Luft an beiden Enden, damit Blasen am Rand nicht angeschnitten werden. */
export const AXIS_PADDING_FRACTION = 0.06;

export interface GrowthDomain {
  /** Untere Achsengrenze inkl. Luft — für die Achsen-Domäne. */
  min: number;
  /** Obere Achsengrenze inkl. Luft — für die Achsen-Domäne. */
  max: number;
  /** Untere Grenze ohne Luft — hierauf werden Ausreißer gepinnt. */
  lowBound: number;
  /** Obere Grenze ohne Luft — hierauf werden Ausreißer gepinnt. */
  highBound: number;
  /** true, wenn mindestens ein Wert außerhalb liegt und gepinnt wird. */
  clamped: boolean;
  /** Anzahl der Werte unterhalb bzw. oberhalb der Grenzen. */
  lowOutliers: number;
  highOutliers: number;
}

/**
 * Empirisches Quantil mit linearer Interpolation („Typ 7", die Voreinstellung
 * von R und NumPy). `sorted` muss aufsteigend sortiert und nicht leer sein.
 */
export function quantileSorted(sorted: number[], q: number): number {
  const n = sorted.length;
  if (n === 0) return Number.NaN;
  if (n === 1) return sorted[0]!;
  const h = (n - 1) * q;
  const lo = Math.floor(h);
  const hi = Math.min(lo + 1, n - 1);
  const frac = h - lo;
  return sorted[lo]! + frac * (sorted[hi]! - sorted[lo]!);
}

/**
 * Achsen-Domäne für die Wachstumswerte.
 *
 * @param values   Wachstumswerte der dargestellten Blasen (als Faktor, nicht
 *                 in Prozent: 0,05 = +5 %).
 * @param mustInclude Werte, die sichtbar bleiben müssen — vor allem die
 *                 Wachstums-Schwelle: Ohne sie stünde der Quadranten-Trenner
 *                 außerhalb des Bildes und die Matrix wäre sinnlos.
 * @param full     true = keine Begrenzung („volle Spanne" in der Oberfläche).
 */
export function computeGrowthDomain(
  values: number[],
  mustInclude: number[] = [],
  full = false,
): GrowthDomain {
  const finite = values.filter((v) => Number.isFinite(v));
  const anchors = mustInclude.filter((v) => Number.isFinite(v));

  if (finite.length === 0) {
    // Keine Blasen: Achse nur um die Anker herum aufspannen.
    const base = anchors.length > 0 ? anchors : [0];
    const lo = Math.min(...base) - AXIS_MIN_HALF_SPAN;
    const hi = Math.max(...base) + AXIS_MIN_HALF_SPAN;
    return {
      min: lo,
      max: hi,
      lowBound: lo,
      highBound: hi,
      clamped: false,
      lowOutliers: 0,
      highOutliers: 0,
    };
  }

  const sorted = [...finite].sort((a, b) => a - b);
  const dataMin = sorted[0]!;
  const dataMax = sorted[sorted.length - 1]!;

  let lowBound = dataMin;
  let highBound = dataMax;

  if (!full) {
    const q1 = quantileSorted(sorted, 0.25);
    const q3 = quantileSorted(sorted, 0.75);
    const iqr = q3 - q1;
    // Die Zäune greifen nur nach innen: Liegen sie außerhalb der Daten,
    // bleibt die volle Spanne stehen und es wird nichts gepinnt. Das ist der
    // Fall „alle Werte liegen eng beieinander".
    lowBound = Math.max(dataMin, q1 - AXIS_OUTLIER_IQR_FACTOR * iqr);
    highBound = Math.min(dataMax, q3 + AXIS_OUTLIER_IQR_FACTOR * iqr);
  }

  // Anker (Schwellenlinie) müssen ins Bild — sie erweitern die Grenzen nur.
  for (const a of anchors) {
    lowBound = Math.min(lowBound, a);
    highBound = Math.max(highBound, a);
  }

  const lowOutliers = finite.filter((v) => v < lowBound).length;
  const highOutliers = finite.filter((v) => v > highBound).length;

  if (highBound - lowBound === 0) {
    lowBound -= AXIS_MIN_HALF_SPAN;
    highBound += AXIS_MIN_HALF_SPAN;
  }

  const pad = (highBound - lowBound) * AXIS_PADDING_FRACTION;

  return {
    min: lowBound - pad,
    max: highBound + pad,
    lowBound,
    highBound,
    clamped: lowOutliers + highOutliers > 0,
    lowOutliers,
    highOutliers,
  };
}

/**
 * Zeichenposition eines Wachstumswerts: innerhalb der Grenzen unverändert,
 * außerhalb auf den Rand gepinnt. `pinned` sagt der Darstellung, dass sie den
 * **echten** Wert danebenschreiben muss — sonst ginge Information verloren.
 */
export function pinGrowth(
  value: number,
  domain: GrowthDomain,
): { y: number; pinned: "low" | "high" | null } {
  if (!Number.isFinite(value)) return { y: 0, pinned: null };
  if (value < domain.lowBound) return { y: domain.lowBound, pinned: "low" };
  if (value > domain.highBound) return { y: domain.highBound, pinned: "high" };
  return { y: value, pinned: null };
}

/**
 * Normalisierung für die Kundensuche: Groß-/Kleinschreibung und Umlaute
 * dürfen kein Hindernis sein — „Müller" muss auch mit „muller" gefunden
 * werden.
 */
export function normalizeForSearch(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ß/g, "ss");
}
